import { useState } from "react";
import { Wand2, Loader, ChevronDown, ChevronUp, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";


const OBJECTION_LABELS = {
  price_too_low: "Price too low",
  no_payments_need_cash: "Don't want payments, need cash now",
  talking_to_others: "Talking to other buyers",
  need_time_to_think: "Need time to think",
  want_full_asking: "Want full asking price",
  concerned_about_taxes: "Concerned about taxes",
  want_to_consult_attorney: "Want to consult attorney",
  worry_about_default: "Worried about buyer default",
};


function _money(n) {
  if (typeof n !== "number") return "$0";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}


export default function NegotiationAssistantPanel({ opp }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState({});
  const [stageGated, setStageGated] = useState(false);

  const generate = async () => {
    setBusy(true);
    setStageGated(false);
    try {
      const r = await api.post(`/documents/negotiation/${opp.oid}`, {});
      if (r.data.error) {
        toast.error(r.data.error.slice(0, 200));
        return;
      }
      setData(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "stage_gated") {
        setStageGated(detail);
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else {
        toast.error("Negotiation assistant unavailable");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ae-card p-5 space-y-4" data-testid="negotiation-assistant-panel">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-emerald-400" />
          <h3 className="font-heading text-lg">Negotiation Assistant</h3>
        </div>
        <button onClick={generate} disabled={busy} data-testid="negotiation-generate"
                className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25 inline-flex items-center gap-1.5">
          {busy ? <Loader className="h-3 w-3 animate-spin" /> : <Wand2 className="h-3 w-3" />}
          {busy ? "Generating..." : data ? "Regenerate" : "Generate"}
        </button>
      </div>

      <p className="text-xs text-slate-500">
        Claude-powered draft offer, alternative structures, objection responses, and follow-up cadence based on this opp's signals + your deal preferences.
      </p>

      {stageGated && (
        <div className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-sm p-3 flex items-start gap-2"
             data-testid="negotiation-stage-gated">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">Locked at current stage: <span className="font-mono">{stageGated.current_stage || "n/a"}</span></div>
            <div className="mt-1">{stageGated.message}</div>
          </div>
        </div>
      )}

      {!data && !busy && !stageGated && (
        <div className="text-xs text-slate-500 italic">
          Click Generate to draft the opening offer, three alternative structures, and objection responses.
        </div>
      )}

      {data && (
        <div className="space-y-4" data-testid="negotiation-output">
          {/* Opening offer */}
          {data.opening_offer && (
            <div className="border border-emerald-500/30 rounded-sm bg-emerald-500/5 p-3" data-testid="negotiation-opening">
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Recommended opening offer</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                <div><span className="text-slate-500 text-[10px] uppercase block">Price</span><span className="font-mono text-slate-100 tabular-nums">{_money(data.opening_offer.purchase_price)}</span></div>
                <div><span className="text-slate-500 text-[10px] uppercase block">Down</span><span className="font-mono text-slate-100 tabular-nums">{data.opening_offer.down_payment_pct}% / {_money(data.opening_offer.down_payment_amount)}</span></div>
                <div><span className="text-slate-500 text-[10px] uppercase block">Carry</span><span className="font-mono text-slate-100 tabular-nums">{_money(data.opening_offer.seller_carry_amount)}</span></div>
                <div><span className="text-slate-500 text-[10px] uppercase block">Rate</span><span className="font-mono text-slate-100 tabular-nums">{data.opening_offer.interest_rate}%</span></div>
                <div><span className="text-slate-500 text-[10px] uppercase block">Amort</span><span className="font-mono text-slate-100 tabular-nums">{data.opening_offer.amortization_years}y</span></div>
                <div><span className="text-slate-500 text-[10px] uppercase block">Balloon</span><span className="font-mono text-slate-100 tabular-nums">{data.opening_offer.balloon_years}y</span></div>
              </div>
              <div className="text-[11px] text-slate-400 mt-3 italic leading-relaxed border-l-2 border-emerald-500/40 pl-2">
                {data.opening_offer.rationale}
              </div>
            </div>
          )}

          {/* Alternatives */}
          {data.alternatives?.length > 0 && (
            <div className="space-y-1.5" data-testid="negotiation-alternatives">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Alternative structures, ranked</div>
              {data.alternatives.map((alt, i) => (
                <div key={i} className="border border-white/8 rounded-sm bg-white/2 p-2.5"
                     data-testid={`negotiation-alt-${i}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-sm font-medium text-slate-200">
                      <span className="text-emerald-400 font-mono tabular-nums">#{alt.rank}</span> {alt.label}
                    </div>
                    <span className="text-[10px] font-mono text-amber-300 tabular-nums">
                      {alt.acceptance_probability_pct}% likely
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">{alt.summary}</div>
                  <div className="text-[11px] text-emerald-300/80 mt-1 italic">Why seller might accept: {alt.why_seller_might_accept}</div>
                </div>
              ))}
            </div>
          )}

          {/* Objection responses */}
          {data.objection_responses && (
            <div className="space-y-1.5" data-testid="negotiation-objections">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Pre-written objection responses</div>
              {Object.entries(data.objection_responses).map(([key, body]) => (
                <div key={key} className="border border-white/8 rounded-sm bg-white/2"
                     data-testid={`negotiation-objection-${key}`}>
                  <button onClick={() => setOpen({ ...open, [key]: !open[key] })}
                          className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left">
                    <span className="text-sm text-slate-200">{OBJECTION_LABELS[key] || key}</span>
                    {open[key] ? <ChevronUp className="h-3.5 w-3.5 text-slate-500" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-500" />}
                  </button>
                  {open[key] && (
                    <div className="px-3 pb-3 pt-1 border-t border-white/5 text-xs text-slate-300 leading-relaxed">
                      {body}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Timeline */}
          {data.timeline?.length > 0 && (
            <div className="space-y-1" data-testid="negotiation-timeline">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Follow-up cadence</div>
              {data.timeline.map((t, i) => (
                <div key={i} className="flex items-baseline gap-3 text-xs">
                  <span className="font-mono text-emerald-400 tabular-nums shrink-0">Day {t.day}</span>
                  <span className="text-slate-300">{t.action}</span>
                </div>
              ))}
            </div>
          )}

          {/* Disclaimer */}
          {data.disclaimer && (
            <div className="text-[11px] text-slate-500 italic border-l-2 border-amber-500/40 pl-2 leading-relaxed"
                 data-testid="negotiation-disclaimer">
              {data.disclaimer}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
